<script>
export default {
  name: 'OrderHistory'
}
</script>

<script setup>
import { ref, reactive, onMounted, onUnmounted, nextTick, watch } from 'vue';

import Pagination from '@parts/Pagination.vue';
import OrderNavigation from '@parts/OrderNavigation.vue';
import GoodsList from '@parts/GoodsList.vue';
import { dateUtil } from '@functions/dateUtil.js';
import { modalUtil } from '@functions/modalUtil.js';
import DatePicker from '@parts/DatePicker.vue';
import axios from 'axios';
import { apiError } from '@functions/apiError.js';
import ApiErrorModal from '@parts/ApiErrorModal.vue';
import HistoryAddToCartConfirm1Modal from './modal/HistoryAddToCartConfirm1Modal.vue';
import HistoryAddToCartConfirm2Modal from './modal/HistoryAddToCartConfirm2Modal.vue';
import { url } from '@/config/urls';
import { message } from '@functions/message.js';
import { openPdf } from '@functions/pdf.js';
import {
  createHardScannerKeywordWatcher,
  resolveBarcodeParams,
  toHalfWidthDigits,
} from '@functions/barcodeScan.js';
import { Capacitor } from '@capacitor/core';
import { useRouter } from 'vue-router';

const router = useRouter();
// 外部定義メソッド
const { comparisonDateString, checkDateFormat, examValidDate } = dateUtil();
const { showModal, switchFromCurrentModal } = modalUtil();

// 画面ID
const screenId = ref("OrderHistory");

// エラーメッセージの状態管理
const errorMessages = ref([]);

// 定数定義
const pageSizeArray = [50, 100, 200];

// Prop定義
const props = defineProps({
  userDataUrl: {
    type: String,
    default: url.api.userData
  },
  orderHistoryListApiUrl: {
    type: String,
    default: url.api.orderHistory.list
  },
  orderHistoryPdfApiUrl: {
    type: String,
    default: url.api.orderHistoryPdf
  },
  orderFavoriteListApiUrl: {
    type: String,
    default: url.api.orderFavorite.list
  },
  orderCartCountApiUrl: {
    type: String,
    default: url.api.orderCart.count
  },
  orderCartAddApiUrl: {
    type: String,
    default: url.api.orderCart.add
  },
  goodsTypesApiUrl: {
    type: String,
    default: url.api.goodsTypes
  },
  loginUrl: {
    type: String,
    default: url.login
  },
  errorUrl: {
    type: String,
    default: url.error
  },
  loadingIcon: {
    type: String,
    default: url.static.loadingIcon
  }
});

// ユーザー情報
const user = reactive({
  isPermittedOrder: null,  // null: 未取得, true: 発注, false: 在庫問合せ
  deliveryDateAllowedFlg: null  // null: 未取得, 1: 納品日指定可能, 0: 納品日指定不可
});

// ユーザー情報取得APIの呼び出し
const getUserInfo = async () => {
  try {
    const response = await axios.get(props.userDataUrl);
    user.isPermittedOrder = response.data.response.isPermittedOrder;
    user.deliveryDateAllowedFlg = response.data.response.deliveryDateAllowedFlg;
  } catch (error) {
    apiError(error, errorMessages);
  }
}

// 商品一覧取得APIリクエストパラメータ
const condition = ref({
  startDate: '',
  endDate: '',
  words: '',
  goodsFavId: '',
  goodsTypeCode: '',
  dispensingPackagingUnitCode: null,
  janCode: null,
  size: 50,
  page: 1
});

// フォーム入力値
const formInputs = ref({
  startDate: '',
  endDate: '',
  words: '',
  goodsFavId: '',
  goodsTypeCode: ''
});

// 検索キーワード入力欄の要素参照
const wordsInputRef = ref(null);

// エラー
const errors = ref({
  date: '',
  words: '',
  goodsFavId: '',
  goodsTypeCode: ''
});

// ページネーション
const pagingParameter = ref({
  pageSize: 50,
  pageNum: 1
});
const totalPage = ref(1);

// 商品分類マスタデータ
const goodsTypes = ref([]);

// お気に入りグループ
const goodsFavorites = ref([]);

// 検索結果
const goodsList = ref([]);
const totalNum = ref(0);
const goodsQuantityList = ref([]);

// ローディングフラグ
const isLoading = ref(false);

// ローディングフラグ更新
const handleUpdateLoading = (loading) => {
  isLoading.value = loading;
}

// エラー処理
const handleApiError = (error) => {
	apiError(error, errorMessages);
};

// カート商品 品目数
const cartGoodsTotalRecord = ref(0);
// カート商品集計
const countCartGoods = () => {
  axios.get(props.orderCartCountApiUrl)
    .then((response) => {
      cartGoodsTotalRecord.value = parseInt(response.data.response.totalNum);
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// チェック済みリスト
const checkedList = ref([]);
// 他ページ込チェック済みリスト
const allCheckedList = ref([]);

// チェック済みリスト更新
const handleUpdateCheckedList = (updatedList) => {
  checkedList.value = updatedList;
}
//　他ページ込チェック済みリスト更新
const handleUpdateAllCheckedList = (updatedList) => {
  allCheckedList.value = updatedList;
}

// お気に入りマスタデータ取得処理
const getGoodsFavorites = () => {
  axios.get(props.orderFavoriteListApiUrl)
    .then((response) => {
      goodsFavorites.value = response.data.response.goodsFavorites;
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// 商品分類マスタデータ取得処理
const getGoodsTypes = () => {
  axios.get(props.goodsTypesApiUrl)
    .then((response) => {
      goodsTypes.value = response.data.response.goodsTypes;
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// 商品一覧取得処理
const getGoodsList = async() => {
  handleUpdateLoading(true);
  try {
    const response = await axios.get(
      props.orderHistoryListApiUrl
      , { params: condition.value }
    );
    if (response.data.response.goodsList) {
      goodsList.value = response.data.response.goodsList;
    }
    totalNum.value = parseInt(response.data.response.totalNum);
    pagingParameter.value.pageNum = parseInt(response.data.response.currentPage);
    totalPage.value = Math.ceil(totalNum.value / pagingParameter.value.pageSize);
    totalPage.value = totalPage.value ? totalPage.value : 1;

    // allCheckedListとgoodsListの両方に含まれるorderInfoIdのみをcheckedListに設定
    checkedList.value = allCheckedList.value.filter(id => 
      goodsList.value.map(item => item.orderInfoId).includes(id)
    );
  } catch(error) {
    apiError(error, errorMessages);
  } finally {
    handleUpdateLoading(false);
  };
}

// 検索キーワード入力欄Enterキー押下時の処理
const onKeyDownEnterWords = (event) => {
  if (isLoading.value) {
    event.preventDefault();
    return;
  }
  if (hardScannerKeywordWatcher.isScanDetectionPending()) {
    event.preventDefault();
    return;
  }
  // 検索時フォーカスアウト(仮想キーボードの非表示)
  event.target.blur();

  clickSearchBtn();
}

// ボタンクリック
const clickSearchBtn = async() => {
  handleUpdateLoading(true);
  dateValidation();
  wordsValidation();
  goodsFavoritesValidation();
  goodsTypesValidation();
  if (Object.values(errors.value).some(value => value !== '')) {
    handleUpdateLoading(false);
    return;
  }
  setCondition();
  await getGoodsList();
  handleUpdateLoading(false);
}

const focusWordsInput = () => {
  const isNativeApp = Capacitor.isNativePlatform();
  if (isNativeApp) {
    return;
  }
  nextTick(() => {
    if (wordsInputRef.value) {
      wordsInputRef.value.focus();
    }
  });
};

const onBarcodeScanned = async () => {
  formInputs.value.words = toHalfWidthDigits(formInputs.value.words || '');
  const raw = formInputs.value.words;
  const parsed = resolveBarcodeParams(raw);
  if (!parsed.ok) {
    errors.value.words = parsed.error;
    formInputs.value.words = '';
    focusWordsInput();
    return;
  }
  errors.value.words = '';
  formInputs.value.words = '';
  dateValidation();
  wordsValidation();
  goodsFavoritesValidation();
  goodsTypesValidation();
  if (Object.values(errors.value).some((value) => value !== '')) {
    focusWordsInput();
    return;
  }
  setCondition();
  condition.value.words = null;
  condition.value.janCode = parsed.janCode;
  condition.value.dispensingPackagingUnitCode = parsed.dispensingPackagingUnitCode;
  await getGoodsList();
  focusWordsInput();
};

const hardScannerKeywordWatcher = createHardScannerKeywordWatcher({
  getKeyword: () => formInputs.value.words,
  onScanComplete: () => {
    void onBarcodeScanned();
  },
});

/** コピペではハードスキャナ自動検索を走らせない */
const skipHardScanAfterPaste = ref(false);

const markKeywordPasteForHardScan = () => {
  skipHardScanAfterPaste.value = true;
  hardScannerKeywordWatcher.cancelPendingScan();
};

const onKeywordFieldInput = (event) => {
  if (event.inputType === 'insertFromPaste') {
    markKeywordPasteForHardScan();
  }
};

watch(
  () => formInputs.value.words,
  (newVal) => {
    if (skipHardScanAfterPaste.value) {
      skipHardScanAfterPaste.value = false;
      hardScannerKeywordWatcher.cancelPendingScan();
      return;
    }
    hardScannerKeywordWatcher.onKeywordChange(newVal);
  }
);

onUnmounted(() => hardScannerKeywordWatcher.dispose());

// 発注権限取得後にメイン DOM が表示されてからキーワード欄へフォーカス
watch(
  () => user.isPermittedOrder,
  (isPermitted) => {
    if (isPermitted === true) {
      focusWordsInput();
    }
  },
  { flush: 'post' }
);

const clickClearBtn = () => {
  formInputs.value.words = '';
  formInputs.value.goodsFavId = '';
  formInputs.value.goodsTypeCode = '';
  condition.value.dispensingPackagingUnitCode = null;
  condition.value.janCode = null;
  // 期間の初期値設定（開始日：今日、終了日：今日）
  setStartDateAndEndDate();
}

// ページネーション処理
const onChangePageNum = (page) => {
  if (!Number.isFinite(page)) {
    return;
  }
  condition.value.page = page;
  getGoodsList();

  pagingParameter.value.pageNum = page;

  window.scroll({
      top: 0,
      behavior: 'smooth'
  });
}

const onChangePageSize = (selected) => {
  condition.value.page = 1;
  condition.value.size = selected;
  getGoodsList();

  pagingParameter.value.pageNum = 1;
  pagingParameter.value.pageSize = selected;

}

// 検索条件のセット
const setCondition = () => {
  condition.value.startDate = formInputs.value.startDate.replaceAll('/', '-') || null;
  condition.value.endDate = formInputs.value.endDate.replaceAll('/', '-') || null;
  condition.value.words = formInputs.value.words || null;
  condition.value.goodsFavId = formInputs.value.goodsFavId || null;
  condition.value.goodsTypeCode = formInputs.value.goodsTypeCode || null;
  condition.value.dispensingPackagingUnitCode = null;
  condition.value.janCode = null;
  condition.value.size = pagingParameter.value.pageSize || 50;
  condition.value.page = 1;
  pagingParameter.value.pageNum = 1;
}

// 日付バリデーション
const dateValidation = () => {
  errors.value.date = '';
  // 開始日
  if (formInputs.value.startDate) {
    if (!checkDateFormat(formInputs.value.startDate)) {
      errors.value.date = message.error.validation.date.format;
    }else if(!examValidDate(formInputs.value.startDate)){
      errors.value.date = message.error.validation.date.validity;
    }
  }
  // 終了日
  if (formInputs.value.endDate){
    if (!checkDateFormat(formInputs.value.endDate)) {
      errors.value.date = message.error.validation.date.format;
    }else if(!examValidDate(formInputs.value.endDate)){
      errors.value.date = message.error.validation.date.validity;
    }
  }
  // 比較
  if (formInputs.value.startDate && formInputs.value.endDate && !errors.value.date) {
    if (comparisonDateString(formInputs.value.startDate, '>', formInputs.value.endDate)) {
      let errorMessage = message.error.validation.date.comparison;
      errorMessage = errorMessage.replace('{end}', '終了日');
      errorMessage = errorMessage.replace('{start}', '開始日');
      errors.value.date = errorMessage;
    }
  }
}

// 検索キーワードバリデーション
const wordsValidation = () => {
  const maxLength = 255;
  errors.value.words = '';
  if (formInputs.value.words.length > maxLength) {
    let errorMessage = message.error.validation.string.length;
    errorMessage = errorMessage.replace('{max}', maxLength);
    errors.value.words = errorMessage;
  }
}

// お気に入りバリデーション
const goodsFavoritesValidation = () => {
  errors.value.goodsFavId = '';
  if (!formInputs.value.goodsFavId) { return; }
  if (!goodsFavorites.value.some(goodsFavorite => goodsFavorite.id === formInputs.value.goodsFavId)) {
    errors.value.goodsFavId = message.error.not_found;
  }
}

// 商品分類バリデーション
const goodsTypesValidation = () => {
  errors.value.goodsTypeCode = '';
  if (!formInputs.value.goodsTypeCode) { return; }
  if (!goodsTypes.value.some(goodsType => goodsType.code === formInputs.value.goodsTypeCode)) {
    errors.value.goodsTypeCode = message.error.not_found;
  }
}

// 「発注履歴帳票」押下時処理
const handleClickHistoryPdf = async() => {
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const today = new Date();
  const twoYearsAgo = new Date(today.getFullYear() - 2, today.getMonth(), today.getDate() + 1);
  
  // リクエストパラメータの設定
  const params = {
    startDate: condition.value.startDate || formatDate(twoYearsAgo),
    endDate: condition.value.endDate || formatDate(today),
    orderInfoIds: goodsList.value.map(item => item.orderInfoId),
    size: pagingParameter.value.pageSize,
    page: condition.value.pageNum,
    format: 'pdf',
    filename: 'report'
  };

  openPdf(router, props.orderHistoryPdfApiUrl, { params, showHeader: false });
}

// 「カートに追加する」押下時処理
const onClickHistoryAddToCart = () => {
  // 同一商品がチェックされていないか確認
  if (confirmGoodsDuplicate()) {
    showModal('historyAddToCartConfirm1Modal');
  } else {
    // カートに同一商品が入っているか確認
    confirmCartGoods();
  }
}

// checkedListをキーにして、チェックに同一商品があるかを確認する
const confirmGoodsDuplicate = () => {
  let isDuplicate = false;
  goodsQuantityList.value = [];
  const janCodeMap = new Map(); // janCodeをキーとして数量を管理
  
  goodsList.value.forEach(item => {
    if (checkedList.value.includes(item.orderInfoId)) {
      const janCode = item.janCode;
      const quantity = parseInt(item.orderQuantity) || 0;
      
      if (janCodeMap.has(janCode)) {
        // 同一janCodeが既に存在する場合、数量を加算
        const existingData = janCodeMap.get(janCode);
        existingData.quantity += quantity;
        isDuplicate = true;
      } else {
        // 新規janCodeの場合、Mapに追加
        janCodeMap.set(janCode, {
          janCode: janCode,
          quantity: quantity
        });
      }
    }
  });
  
  // MapからgoodsQuantityListに変換
  goodsQuantityList.value = Array.from(janCodeMap.values());
  
  return isDuplicate;
}


// カートに同一商品が入っているか確認
const confirmCartGoods = () => {
  // カートに同一商品が入っているか確認
  const janCodeList = goodsQuantityList.value.map(item => item.janCode);
  handleUpdateLoading(true);
  axios.get(props.orderCartCountApiUrl, { params: { janCodeList: janCodeList } })
  .then((response) => {
    if (response.data.response.totalNum === 0) {
      // 重複なしのため、警告なしで追加
      historyAddToCart();
    } else {
      // 重複ありのため、警告モーダルを表示
      handleUpdateLoading(false);
      switchFromCurrentModal('historyAddToCartConfirm2Modal');
    }
  })
  .catch((error) => {
    handleUpdateLoading(false);
    apiError(error, errorMessages);
  });
}

// カートに追加
const historyAddToCart = () => {

  handleUpdateLoading(true);
  axios.post(props.orderCartAddApiUrl, { goodsList: goodsQuantityList.value })
  .then((response) => {

    // 画面内のチェック状態を解除
    goodsList.value.forEach(item => {
      if (checkedList.value.includes(item.orderInfoId)) {
        checkedList.value.splice(checkedList.value.indexOf(item.orderInfoId), 1);
      }
      if (allCheckedList.value.includes(item.orderInfoId)) {
        allCheckedList.value.splice(allCheckedList.value.indexOf(item.orderInfoId), 1);
      }
    });
    
    switchFromCurrentModal('addToCartCompleteModal');
    countCartGoods();

  })
  .catch((error) => {
    apiError(error, errorMessages);
  })
  .finally(() => {
    handleUpdateLoading(false);
  });
}


// 期間の初期値設定
const setStartDateAndEndDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const formatDate = `${year}/${month}/${day}`;
  formInputs.value.startDate = formatDate;
  formInputs.value.endDate = formatDate;
}

// 初期処理
onMounted(async () => {
  await getUserInfo();
  if (!user.isPermittedOrder) {
    return;
  }

  // 期間の初期値設定（開始日：今日、終了日：今日）
  setStartDateAndEndDate();

  countCartGoods();
  getGoodsFavorites();
  getGoodsTypes();

  // デフォルトで当日分履歴を表示
  setCondition();
  getGoodsList();
});
</script>

<template>

  <div id="contents1" class="bg1" v-if="user.isPermittedOrder">
    <OrderNavigation
      :screenId = "screenId"
      :cartGoodsTotalRecord = cartGoodsTotalRecord
    ></OrderNavigation>
    <h2 class="com_title1 com_mgb5">発注履歴</h2>
    <div class="com_result1">
      <div class="com_inner2">
        <div class="datepicker-group1">
          <div>
            <label>期間</label>
            <div>
              <DatePicker
                v-model="formInputs.startDate"
                identifier="js-datepicker1" ref="startDate"
                :class="{ error: errors.date }"
              ></DatePicker>
            </div>
            <span>〜</span>
            <div>
              <DatePicker
                v-model="formInputs.endDate"
                identifier="js-datepicker2" ref="endDate"
                :class="{ error: errors.date }"
              ></DatePicker>
            </div>
          </div>
        </div>
        <div class="com_validate_msg1 st1 error" v-if="errors.date">{{ errors.date }}</div>
        <div class="group6">
          <div>
            <input type="text" placeholder="メーカー名、商品名、商品コード、GTIN"
              ref="wordsInputRef"
              class="form-control com_form1" v-model="formInputs.words"
              :class="{ error: errors.words }"
              @input="onKeywordFieldInput"
              @paste="markKeywordPasteForHardScan"
              @keydown.enter="onKeyDownEnterWords" enterkeyhint="search">
            <div class="com_validate_msg1 error" v-if="errors.words">{{ errors.words }}</div>
          </div>
        </div>
        <div class="group4">
          <select class="form-select com_form1" v-model="formInputs.goodsFavId" :class="{ error: errors.goodsFavId }">
            <option value="">お気に入りグループ</option>
            <option v-for="fav in goodsFavorites" :key="fav.id" :value="fav.id">
              {{ fav.name }}
            </option>
          </select>
          <select class="form-select com_form1" v-model="formInputs.goodsTypeCode" :class="{ error: errors.goodsTypeCode }">
            <option value="" selected>商品分類:すべて</option>
            <option v-for="goodsType in goodsTypes" :key="goodsType.code" :value="goodsType.code">
              {{ goodsType.name }}
            </option>
          </select>
          <button type="button" class="btn com_btn1" @click.prevent="clickSearchBtn">検索</button>
          <button type="button" class="btn com_btn2" @click.prevent="clickClearBtn">クリア</button>
        </div>
        <div>
          <div class="com_validate_msg1 error" v-if="errors.goodsFavId">{{ errors.goodsFavId }}</div>
          <div class="com_validate_msg1 error" v-if="errors.goodsTypeCode">{{ errors.goodsTypeCode }}</div>
        </div>
      </div>
    </div>

    <div class="com_inner4 clearfix">
      <template v-if="goodsList.length === 0">
        <p class="com_fs16 text-center">検索項目を入力してください。</p>
      </template>
      <template v-else>
        <p class="com_fs16 text-center order-results-text">検索結果は以下の通りです</p>

        <Pagination
          :isShowFirstAndLastBtn="true"
          :isShowPaginationInfo="true"
          :totalNum="totalNum"
          :currentPage="pagingParameter.pageNum"
          :totalPage="totalPage"
          :pageSize="pagingParameter.pageSize"
          :pageSizeSelectList="pageSizeArray"
          :screenId = "screenId"
          @clickHistoryPdf="handleClickHistoryPdf"
          @changePageNum="onChangePageNum"
          @changePageSize="onChangePageSize"
        ></Pagination>

        <GoodsList
          :screenId = "screenId"
          :isPermittedOrder = user.isPermittedOrder
          :deliveryDateAllowedFlg = user.deliveryDateAllowedFlg
          :goodsList = goodsList
          :goodsTypes = goodsTypes
          :goodsFavorites = goodsFavorites
          :checkedList = checkedList
          :allCheckedList = allCheckedList
          @updateLoading="handleUpdateLoading"
          @updateCheckedList="handleUpdateCheckedList"
          @updateAllCheckedList="handleUpdateAllCheckedList"
          @countCartGoods="countCartGoods"
          @historyAddToCart="onClickHistoryAddToCart"
          @apiError="handleApiError"
          @resetCartState="getGoodsList"
        ></GoodsList>

        <Pagination
          :isShowFirstAndLastBtn="true"
          :isShowPaginationInfo="true"
          :totalNum="totalNum"
          :currentPage="pagingParameter.pageNum"
          :totalPage="totalPage"
          :pageSize="pagingParameter.pageSize"
          :pageSizeSelectList="pageSizeArray"
          @changePageNum="onChangePageNum"
          @changePageSize="onChangePageSize"
        ></Pagination>

      </template>
    </div>
  </div>

  <HistoryAddToCartConfirm1Modal
    @confirmCartGoods="confirmCartGoods"
  ></HistoryAddToCartConfirm1Modal>

  <HistoryAddToCartConfirm2Modal
    @historyAddToCart="historyAddToCart"
  ></HistoryAddToCartConfirm2Modal>

  <ApiErrorModal
    :messages="errorMessages"
  ></ApiErrorModal>

  <Transition>
    <div class="loading-animation" v-show="isLoading">
      <div class="show-icon-box">
        <img :src="props.loadingIcon" />
      </div>
    </div>
  </Transition>

</template>
